import React, {useState, useContext, useEffect, useRef, useMemo, useCallback} from 'react'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import FilterListIcon from '@material-ui/icons/FilterList'
import './SearchItem.scss'
import {
  Grid,
  Typography,
  Table,
  TableHead,
  TableCell,
  TableRow,
  Checkbox,
  TableBody,
} from '@material-ui/core'
import GroupSelect from './GroupSelect'
import FieldFilter from './FieldFilter'
import {ReservationContext} from '../../context/Reservation/Reservation'
import {UserContext} from '../../context/User'
import _ from 'lodash'
import moment from 'moment'
import LoadingIndicator from '../ReservationConfiguration/LoadingIcon'
import {transformDateFormat} from '../../context/Reservation/util'
import {SettingsContext} from '../../context/Settings/Settings'
import {useModal} from '../../common/Modal'

const SearchItem = () => {
  const {
    loadConfiguration,
    loadEntityObjects,
    loadEntities,
    configuration,
    entityObjects,
    entityFields,
    entities,
    reservation,
    update,
    reserveLimit,
    formatSelectedItems,
  } = useContext(ReservationContext)
  const settings = useContext(SettingsContext)
  const simpleModal = useModal()
  const currentUser = useContext(UserContext)
  const {selected_group, reserved_for, reserve_for_field, date_from, date_to} = reservation // deconstruct to avoid rerenders
  const [search, setSearch] = useState('')
  const refTableScroll = useRef(null)

  const groups = useMemo(() => {
    if (_.isEmpty(reserved_for)) return []
    const _filters = settings.data?.user_access_rules?.can_add?.reservations || []
    return entities.data.filter(f => !!_filters.find(ff => ff === f.key))
  }, [entities, settings, reserved_for])

  const getSelectedGroup = (data, selectedGroup) => {
    let op
    data.forEach(group => {
      if (group.id === selectedGroup) {
        op = group
      }
    })
    return op
  }

  // pre select the group once entity api is loaded
  useEffect(() => {
    if (!entities.isLoading) {
      if (groups.length > 0 && !reservation.selected_group_id) {
        update('selected_group', {...groups[0]})
      } else if (groups.length > 0 && reservation.selected_group_id) {
        update('selected_group', getSelectedGroup(groups, reservation.selected_group_id))
      } else {
        update('selected_group', null)
        // entityObjectsDispatch({type: getReservationEntityItemsActionTypes.RESET})
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, groups, update])

  // load configuration and entity object if selected_group has changed
  useEffect(() => {
    if (selected_group !== null && selected_group?.id) {
      loadConfiguration(selected_group?.id)
      loadEntityObjects(selected_group?.id, {
        page: 1,
        limit: 50,
        start_date: moment(date_from)
          .local()
          .format(),
        end_date: moment(date_to)
          .local()
          .format(),
        query: search,
        reservation_uid: reservation.is_edit ? reservation.reservation_uid : '',
      })
      const el = refTableScroll.current
      if (el) {
        el.scrollTop = 0
      }
    }
  }, [
    selected_group,
    loadEntityObjects,
    loadConfiguration,
    refTableScroll,
    date_from,
    date_to,
    search,
    reservation.is_edit,
    reservation.reservation_uid,
  ])

  const handleChangeGroup = (e, o) => {
    if (!_.isEqual(selected_group, o)) {
      update('selected_group', {...o})
    }
  }

  const handleToggleSelect = (e, object) => {
    let _items = [...reservation.selected_items]
    const index = _items.findIndex(f => f.id === object.id)
    if (index !== -1) {
      _items.splice(index, 1)
    } else {
      _items.push(object)
    }

    if (_items.length > reserveLimit) {
      if (simpleModal) {
        simpleModal.show({
          title: 'Reservation Limit',
          description: `Can't reserve more than ${reserveLimit} items.`,
          buttons: {
            affirmative: {label: 'OK'},
          },
        })
      }
      return
    }
    update('selected_items', _items)
  }

  const handleSearch = e => {
    e.preventDefault()
    setSearch(e.target.elements.search.value)
  }

  const handleApplyFilter = (e, filters) => {
    let _filters = {}

    if (filters.length) {
      filters.forEach(f => {
        _filters[f.key] = f.value
      })
    }

    loadEntityObjects(selected_group.id, {
      page: 1,
      limit: 50,
      filters: _filters,
      formattedDateFrom: moment(date_from)
        .local()
        .format(),
      formattedDateTo: moment(date_to)
        .local()
        .format(),
    })
  }

  // lazy load
  useEffect(() => {
    const el = refTableScroll.current

    const handleScrollEvent = e => {
      if (
        Math.round(e.target.offsetHeight + e.target.scrollTop) >= Math.round(e.target.scrollHeight)
      ) {
        if (!entityObjects.isLoading && entityObjects.page < entityObjects.total_page) {
          loadEntityObjects(selected_group.id, {
            page: entityObjects.page + 1,
            limit: 50,
            query: search,
          })
        }
      }
    }
    if (el) {
      el.addEventListener('scroll', handleScrollEvent)
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScrollEvent)
      }
    }
  }, [refTableScroll, entityObjects, loadEntityObjects, selected_group, search])

  return (
    <div className="c-search-reserve-item">
      <Grid container>
        <Grid item style={{position: 'relative', width: 224}}>
          <GroupSelect
            options={groups}
            loading={entities.isLoading}
            value={selected_group}
            onChange={handleChangeGroup}
          />
        </Grid>
        <Grid item style={{position: 'relative', flex: 1}}>
          <div className="c-search-reserve-item__group-search">
            <form style={{flex: 1, display: 'flex'}} onSubmit={handleSearch}>
              <button
                type="submit"
                style={{
                  transform: 'scale(.85)',
                  width: 'min-content',
                  border: 'none',
                  background: 'unset',
                  padding: 0,
                  outline: 'none',
                }}
              >
                <SearchIcon />
              </button>
              <input
                className="ap-typography ap-typography--sm"
                name="search"
                style={{flex: 1, border: 'none'}}
                type="text"
                placeholder="Search"
              />
            </form>
            <div>
              <FieldFilter
                options={entityFields}
                objects={entityObjects}
                onChange={handleApplyFilter}
              />
            </div>
          </div>
        </Grid>
      </Grid>
      <div
        ref={refTableScroll}
        className="ap-scrollbar ap-table c-search-reserve-item__search-list"
      >
        <Table stickyHeader style={{minWidth: '100%', height: '100%'}}>
          <TableHead>
            <TableRow>
              <TableCell style={{width: 32}}></TableCell>
              {!!selected_group &&
                entityObjects.data?.fields_for_display?.map((f, fi) => (
                  <TableCell
                    key={fi}
                    className="ap-typography ap-typography--light ap-typography--nowrap"
                    style={{padding: 'auto 4px'}}
                  >
                    {f.name}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(selected_group === null ||
              (!entityObjects.isLoading &&
                entityObjects.data?.entity_objects &&
                entityObjects.data?.entity_objects?.length === 0)) && (
              <TableRow className={`ap-table__row ap-table__row--disabled`}>
                <TableCell
                  colSpan={(entityObjects.data?.fields_for_display?.length || 0) + 1}
                  className="ap-typography ap-typography--xs ap-typography--nowrap"
                  style={{padding: 'auto 4px', textAlign: 'center'}}
                >
                  No results found.
                </TableCell>
              </TableRow>
            )}

            {selected_group !== null &&
              entityObjects.data?.entity_objects?.map((o, oi) => {
                return (
                  <TableRow
                    key={oi}
                    className={`ap-table__row ap-table__row--clickable ${
                      !!reservation.selected_items.find(s => s.id === o.id)
                        ? 'ap-table__row--selected'
                        : ''
                    }
                ${o.label !== 'enabled' ? 'ap-table__row--disabled' : ''} `}
                    onClick={e => handleToggleSelect(e, o)}
                  >
                    <TableCell>
                      {o.label === 'enabled' && (
                        <Checkbox
                          checked={!!reservation.selected_items.find(s => s.id === o.id)}
                          size="small"
                          checkedIcon={<CheckCircleIcon style={{color: '#3473ff'}} />}
                        />
                      )}
                    </TableCell>
                    {entityObjects.data?.fields_for_display?.map((f, fi) => {
                      switch (f.field_type) {
                        case 'StatusField':
                          return (
                            <TableCell
                              key={fi}
                              className="ap-typography ap-typography--xs ap-typography--nowrap"
                              style={{padding: 'auto 4px'}}
                            >
                              {o[f.key]?.display_name}
                            </TableCell>
                          )
                        case 'DateField':
                          return (
                            <TableCell
                              key={fi}
                              className="ap-typography ap-typography--xs ap-typography--nowrap"
                              style={{padding: 'auto 4px'}}
                            >
                              {!!o[f.key]
                                ? moment(o[f.key]).format(
                                    `${transformDateFormat(currentUser.account_date_format)}`
                                  )
                                : ''}
                            </TableCell>
                          )
                        default:
                          return (
                            <TableCell
                              key={fi}
                              className="ap-typography ap-typography--xs ap-typography--nowrap"
                              style={{padding: 'auto 4px'}}
                            >
                              {o[f.key]}
                            </TableCell>
                          )
                      }
                    })}
                  </TableRow>
                )
              })}
            <TableRow className={`ap-table__row`}>
              <TableCell
                colSpan={(entityObjects.data?.fields_for_display?.length || 0) + 1}
                style={{padding: 'auto 4px', borderBottom: 'none'}}
              >
                {!!selected_group && entityObjects.page === entityObjects.total_page && (
                  <div
                    className="ap-typography ap-typography--gray ap-typography--xs ap-typography--nowrap"
                    style={{textAlign: 'center'}}
                  >
                    No more items.
                  </div>
                )}

                {entityObjects.isLoading && <LoadingIndicator style={{width: '100%'}} />}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default SearchItem

const DummyList = [
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235', disabled: true},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235', disabled: true},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235', disabled: true},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
  {asset_id: 'AP001', model: 'Macbook Pro 15"', make: 'Apple', serial: '5345235'},
]

const GroupDummyList = [
  {id: 12059, name: 'Assets'},
  {id: 12060, name: 'Trucks'},
  {id: 3, name: 'Buildings'},
  {id: 4, name: 'Locations'},
  {id: 5, name: 'Assets'},
  {id: 6, name: 'Trucks'},
  {id: 7, name: 'Buildings'},
  {id: 8, name: 'Locations'},
]

const GroupFieldList = [
  {id: 1, name: 'Asset ID'},
  {id: 2, name: 'Model'},
  {id: 3, name: 'Make'},
  {id: 4, name: 'Serial'},
]
